import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  UserCircle, Clock, Umbrella, DollarSign, Heart, 
  GraduationCap, Target, Receipt, Bell, TrendingUp, AlertCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { RouteErrorBoundary } from '../../components/RouteErrorBoundary';

const API = process.env.REACT_APP_BACKEND_URL;
// 2026-02-11 (audit P0-2) — Every async surface has a bounded timeout.
// After this, the loading state transitions to a retryable error UI so we
// never render an infinite spinner.
const FETCH_TIMEOUT_MS = 15000;

export default function MyDashboardPage() {
  return (
    <RouteErrorBoundary>
      <MyDashboardPageInner />
    </RouteErrorBoundary>
  );
}

function MyDashboardPageInner() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [stats, setStats] = useState({
    attendance_this_month: 0,
    leave_balance: 0,
    pending_expenses: 0,
    upcoming_training: 0,
    my_goals_progress: 0,
  });

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/employee-portal/dashboard`, { timeout: FETCH_TIMEOUT_MS });
      const d = res.data || {};
      // v2.7.23 response shape: { employee, stats: {pending_leave_requests, pending_expense_claims, pending_training_courses}, announcements, quick_actions }
      // Adapt to the flat shape this page expects while preserving legacy support.
      if (d.stats && typeof d.stats === 'object') {
        setStats({
          attendance_this_month: d.stats.attendance_this_month ?? 0,
          leave_balance: d.stats.leave_balance ?? 0,
          pending_expenses: d.stats.pending_expense_claims ?? d.stats.pending_expenses ?? 0,
          upcoming_training: d.stats.pending_training_courses ?? d.stats.upcoming_training ?? 0,
          my_goals_progress: d.stats.my_goals_progress ?? 0,
        });
      } else if (typeof d.attendance_this_month !== 'undefined') {
        setStats(d);
      }
      setAnnouncements(Array.isArray(d.announcements) ? d.announcements : []);
    } catch (err) {
      const code = err?.response?.status;
      const message = err?.code === 'ECONNABORTED'
        ? 'The dashboard is taking too long to respond. Please retry.'
        : (code === 403
            ? "Employee Portal access denied for your role. Ask an admin to add you to the staff roster."
            : (err?.response?.data?.detail || err?.message || 'Failed to load dashboard'));
      setError({ code: code || 'timeout', message });
      if (code && code !== 403) {
        console.error('Failed to fetch employee dashboard:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const quickActions = [
    { icon: UserCircle, label: 'My Profile', path: '/employee/profile', color: 'bg-emerald-500' },
    { icon: Clock, label: 'Clock In/Out', path: '/employee/attendance', color: 'bg-blue-500' },
    { icon: Umbrella, label: 'Request Leave', path: '/employee/leave', color: 'bg-purple-500' },
    { icon: DollarSign, label: 'View Paystubs', path: '/employee/payroll', color: 'bg-green-500' },
    { icon: Heart, label: 'My Benefits', path: '/employee/benefits', color: 'bg-pink-500' },
    { icon: GraduationCap, label: 'Training Courses', path: '/employee/training', color: 'bg-indigo-500' },
    { icon: Target, label: 'My Goals', path: '/employee/goals', color: 'bg-orange-500' },
    { icon: Receipt, label: 'Submit Expense', path: '/employee/expenses', color: 'bg-yellow-500' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen p-6 bg-gray-50" data-testid="employee-dashboard-loading">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your dashboard...</p>
              <p className="text-xs text-gray-400 mt-2">Will timeout after 15 seconds</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-6 bg-gray-50" data-testid="employee-dashboard-error">
        <div className="max-w-md mx-auto pt-20">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
                <AlertCircle className="h-6 w-6 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Couldn&apos;t load your dashboard</h2>
              <p className="text-sm text-gray-500 mb-4">{error.message}</p>
              <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-6">status {String(error.code)}</div>
              <Button onClick={fetchDashboardData} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="employee-dashboard-retry">
                <RefreshCw className="h-4 w-4 mr-2" /> Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Workspace</h1>
            <p className="text-gray-600 mt-1">Welcome back! Here&apos;s your workspace overview.</p>
          </div>
          <Button onClick={() => navigate('/employee/profile')} className="bg-emerald-500 hover:bg-emerald-600">
            <UserCircle className="h-4 w-4 mr-2" />
            View Profile
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Attendance This Month</CardTitle>
              <Clock className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats.attendance_this_month} days</div>
              <p className="text-xs text-gray-500 mt-1">Working days logged</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Leave Balance</CardTitle>
              <Umbrella className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats.leave_balance} days</div>
              <p className="text-xs text-gray-500 mt-1">Available PTO</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Pending Expenses</CardTitle>
              <Receipt className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats.pending_expenses}</div>
              <p className="text-xs text-gray-500 mt-1">Awaiting approval</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Goals Progress</CardTitle>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{stats.my_goals_progress}%</div>
              <p className="text-xs text-gray-500 mt-1">30-60-90 day goals</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => navigate(action.path)}
                  className="flex flex-col items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-emerald-500 hover:shadow-md transition-all group"
                >
                  <div className={`${action.color} p-3 rounded-lg group-hover:scale-110 transition-transform`}>
                    <action.icon className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 text-center">{action.label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Announcements */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Announcements</CardTitle>
            <Bell className="h-5 w-5 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="bg-blue-500 p-2 rounded-full">
                  <Bell className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Welcome to the Employee Portal!</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    You can now manage your profile, attendance, leave requests, payroll, benefits, training, and more — all in one place.
                  </p>
                  <p className="text-xs text-gray-500 mt-2">Just now</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
